import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import { ThemeParams } from 'telegram-web-app';

import { getWebApp } from '@tg-web/utils';

import { hexToHsl } from '../lib/hexToHsl';
import { setAppBackground } from '../lib/setAppBackground';
import {
  DefaultThemeType,
  tgDarkTheme,
  tgLightTheme,
} from '../model/defaultTheme';
import { TelegramPlatforms } from '../model/tgPlatforms';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  appTheme: 'dark' | 'light';
  setAppBackground: (themeOverride: Partial<DefaultThemeType>) => void;
  setTheme: (theme: Theme) => void;
  theme: Theme;
  themeColors: Required<ThemeParams>;
};

const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
  ? 'dark'
  : 'light';

const initialThemeColors = systemTheme === 'dark' ? tgDarkTheme : tgLightTheme;
const initialDefaultColors: DefaultThemeType = {
  backgroundColor:
    initialThemeColors.secondary_bg_color ?? 'secondary_bg_color',
  bottomBarColor:
    initialThemeColors.bottom_bar_bg_color ?? 'secondary_bg_color',
  headerColor: initialThemeColors.secondary_bg_color ?? 'secondary_bg_color',
};
const initialState: ThemeProviderState = {
  appTheme: systemTheme,
  setAppBackground: setAppBackground(initialDefaultColors, initialThemeColors),
  setTheme: () => null,
  theme: 'system',
  themeColors: initialThemeColors,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () =>
      (localStorage.getItem(storageKey) as Theme) ||
      getWebApp().colorScheme ||
      defaultTheme,
  );
  /*
   * What `system` currently resolves to. Separate from the setting above on
   * purpose: the setting is what the user picked and what is persisted, and
   * this is only what Telegram happens to be showing right now. Collapsing the
   * two is what used to turn `system` into an explicit choice on the first
   * theme change.
   *
   * The module-level `systemTheme` is the browser's prefers-color-scheme and is
   * read once at import, so it is a fallback here rather than the source.
   */
  const [resolvedSystemTheme, setResolvedSystemTheme] = useState<
    'dark' | 'light'
  >(() => getWebApp().colorScheme || systemTheme);
  const appTheme = theme === 'system' ? resolvedSystemTheme : theme;
  const themeColors = appTheme === 'dark' ? tgDarkTheme : tgLightTheme;
  const defaultColors: DefaultThemeType = {
    backgroundColor: themeColors.secondary_bg_color ?? 'secondary_bg_color',
    bottomBarColor: themeColors.bottom_bar_bg_color ?? 'secondary_bg_color',
    headerColor: themeColors.secondary_bg_color ?? 'secondary_bg_color',
  };

  useLayoutEffect(() => {
    getWebApp().disableVerticalSwipes();
    getWebApp().expand();
  }, []);

  useLayoutEffect(() => {
    if (getWebApp().platform === TelegramPlatforms.unknown) {
      return;
    }

    // The resolved theme, not the setting: under `system` resolving to dark
    // this used to take the light branch below and inject the light
    // --custom_light_button_color into :root, where being appended last let it
    // beat the stylesheet's own .dark rule.
    const isDarkTheme = appTheme === 'dark';

    setAppBackground(defaultColors, themeColors)({});

    const style = document.createElement('style');
    const accentColor = hexToHsl(themeColors.accent_text_color);
    const customParamsOverride = [
      `--custom_light_button_color: ${isDarkTheme ? `${accentColor.h} 30% 25%` : `${accentColor.h} 100% 90%`};`,
      `--custom_button_active_color: ${accentColor.h} ${accentColor.s}% ${Math.max(0, accentColor.l - 14)}%;`,
    ].join(' ');
    const stylesForVarsOverride = `
    ${isDarkTheme ? '.dark' : ':root'} {
    ${Object.entries(themeColors).reduce((acc, [key, value]) => {
      const { h, l, s } = hexToHsl(value as string);
      return `${acc}--${key}: ${h} ${s}% ${l}%; `;
    }, customParamsOverride)}
    }`;
    style.textContent = stylesForVarsOverride;
    document.head.append(style);
  }, [appTheme]);

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');
    root.classList.add(appTheme);
  }, [appTheme]);

  useEffect(() => {
    if (theme === 'system') {
      // Resync on subscribe as well as on the event: nothing is listening while
      // the setting is explicit, so the resolved value is stale by the time the
      // user picks System back.
      setResolvedSystemTheme(getWebApp().colorScheme || systemTheme);

      const onThemeChange = () => {
        setResolvedSystemTheme(getWebApp().colorScheme || systemTheme);
      };
      getWebApp().onEvent('themeChanged', onThemeChange);

      return () => {
        getWebApp().offEvent('themeChanged', onThemeChange);
      };
    }
  }, [theme]);

  const value = {
    appTheme,
    setAppBackground: setAppBackground(defaultColors, themeColors),
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    theme,
    themeColors,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
